import { useEffect, useState } from "react";
import { Loader2, FileSignature, Search } from "lucide-react";
import { toast } from "sonner";

import { crmService } from "@/services/crmService";
import { TRANSACTION_TYPES, REPRESENTATION_SIDES } from "@/config/crmOptions";
import { US_STATES } from "@/config/formOptions";
import { Field } from "@/components/profile/FormControls";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";
const empty = { transaction_type: "purchase", representation_side: "buyer", status: "active",
  property_address_line1: "", city: "", state: "", postal_code: "", sale_price: "", expected_closing_date: "" };

export const NewTransactionDialog = ({ open, onOpenChange, presetContact, presetLead, onCreated }) => {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) { setForm(empty); setErrors({}); setQ(""); setResults([]); setSelected(presetContact || null); }
  }, [open, presetContact]);

  useEffect(() => {
    if (selected || q.trim().length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try { const { contacts } = await crmService.listContacts({ search: q.trim(), tab: "all" }); if (active) setResults(contacts.slice(0, 6)); } catch {}
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, selected]);

  const save = async () => {
    if (!selected) return;
    setErrors({}); setSaving(true);
    try {
      await crmService.createTransaction({
        contact_id: selected.id,
        lead_id: presetLead?.id || null,
        transaction_type: form.transaction_type,
        representation_side: form.representation_side,
        status: form.status,
        property_address_line1: form.property_address_line1,
        city: form.city || null,
        state: form.state || null,
        postal_code: form.postal_code || null,
        sale_price: form.sale_price === "" ? null : Math.round(Number(form.sale_price) * 100),
        expected_closing_date: form.expected_closing_date || null,
      });
      toast.success("Transaction created");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      setErrors(parseFieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="new-transaction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-accent" /> New Transaction</DialogTitle>
          <DialogDescription>Turn an active client relationship into a tracked deal.</DialogDescription>
        </DialogHeader>

        {selected ? (
          <div className="flex items-center justify-between rounded-lg border border-brand-navy/30 bg-secondary/40 px-3 py-2" data-testid="tx-selected-contact">
            <span className="text-sm font-medium">{selected.first_name} {selected.last_name}</span>
            {!presetContact && <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Change</Button>}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-lg border border-border px-3">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a contact…" className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="tx-contact-search" />
            </div>
            {results.map((c) => (
              <button key={c.id} onClick={() => setSelected(c)} data-testid={`tx-result-${c.id}`} className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-secondary">
                {c.first_name} {c.last_name}{c.email ? ` · ${c.email}` : ""}
              </button>
            ))}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Type" htmlFor="tx-type">
            <Select value={form.transaction_type} onValueChange={(v) => set("transaction_type", v)}>
              <SelectTrigger id="tx-type" data-testid="tx-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TRANSACTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Representation" htmlFor="tx-side">
            <Select value={form.representation_side} onValueChange={(v) => set("representation_side", v)}>
              <SelectTrigger id="tx-side" data-testid="tx-side"><SelectValue /></SelectTrigger>
              <SelectContent>{REPRESENTATION_SIDES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Status" htmlFor="tx-status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger id="tx-status" data-testid="tx-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="under_contract">Under Contract</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Property address" htmlFor="tx-addr" error={errors.property_address_line1}>
          <Input id="tx-addr" value={form.property_address_line1} onChange={(e) => set("property_address_line1", e.target.value)} data-testid="tx-address" placeholder="123 Main St" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="City" htmlFor="tx-city"><Input id="tx-city" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="tx-city" /></Field>
          <Field label="State" htmlFor="tx-state" error={errors.state}>
            <Select value={form.state || NONE} onValueChange={(v) => set("state", v === NONE ? "" : v)}>
              <SelectTrigger id="tx-state" data-testid="tx-state"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-64"><SelectItem value={NONE}>—</SelectItem>{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="ZIP" htmlFor="tx-zip" error={errors.postal_code}><Input id="tx-zip" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="tx-zip" /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Price ($)" htmlFor="tx-price" error={errors.sale_price}>
            <Input id="tx-price" type="number" min="0" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="tx-price" placeholder="450000" />
          </Field>
          <Field label="Expected closing" htmlFor="tx-close">
            <Input id="tx-close" type="date" value={form.expected_closing_date} onChange={(e) => set("expected_closing_date", e.target.value)} data-testid="tx-closing" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-tx-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !selected} data-testid="new-tx-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Create Transaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
